import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlaylistBrief, PlaylistFolder } from '../utils/redux/playlistSlice'
import { apiBaseURL } from '../utils/api'
import { getAccentById, getGradientById } from '../utils/color'
import './PlaylistSection.scss'

interface PlaylistSectionProps {
  title: string
  items: PlaylistBrief[] | PlaylistFolder[]
  type: 'playlist' | 'mood' | 'style' | 'daytime' | 'business'
}

type FolderType = Exclude<PlaylistSectionProps['type'], 'playlist'>

const TYPE_TO_ROUTE_SEGMENT: Record<
  FolderType,
  'mood' | 'style' | 'daytime' | 'business'
> = {
  mood: 'mood',
  style: 'style',
  daytime: 'daytime',
  business: 'business',
}

const TYPE_TO_UPLOADED_DESTINATION: Record<
  FolderType,
  'moods' | 'styles' | 'daytimes' | 'businesses'
> = {
  mood: 'moods',
  style: 'styles',
  daytime: 'daytimes',
  business: 'businesses',
}

type SectionItem = (PlaylistBrief | PlaylistFolder) & Record<string, unknown>

export default function PlaylistSection({ title, items, type }: PlaylistSectionProps) {
  const navigate = useNavigate()
  const { i18n, t } = useTranslation('home')

  const trackRef = useRef<HTMLDivElement | null>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const isMood = type === 'mood'
  const isTiles = type === 'style'

  const safeItems = useMemo(
    () => (Array.isArray(items) ? (items as SectionItem[]) : []),
    [items],
  )

  const asText = useCallback(
    (value: unknown): string => {
      if (value == null) return ''
      if (typeof value === 'string' || typeof value === 'number') return String(value)
      if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(' ')

      if (typeof value === 'object') {
        const obj = value as Record<string, unknown>
        const lang = (i18n.language || 'en').split('-')[0]

        if (obj[lang] != null) return asText(obj[lang])
        if (obj.en != null) return asText(obj.en)
        if (obj.ru != null) return asText(obj.ru)
        if (obj.fr != null) return asText(obj.fr)
        if (obj.uk != null) return asText(obj.uk)

        const firstString = Object.values(obj).find((v) => typeof v === 'string')
        return firstString ? String(firstString) : ''
      }

      return ''
    },
    [i18n.language],
  )

  const normalizeTitle = useCallback((value: string) => {
    return String(value || '')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim()
  }, [])

  const getId = useCallback((item: SectionItem): string => {
    const id = (item._id ?? item.id ?? '') as string | number
    return String(id || '')
  }, [])

  const getTitleText = useCallback(
    (item: SectionItem) => normalizeTitle(asText(item.title ?? item.name)),
    [asText, normalizeTitle],
  )

  const getSubtitleText = useCallback(
    (item: SectionItem) =>
      asText(
        item.subtitle ??
          item.description ??
          (type === 'playlist'
            ? t('labels.madeForYou', { defaultValue: 'Made for you' })
            : t('labels.category', { defaultValue: 'Category' })),
      ),
    [asText, t, type],
  )

  const getImageUrl = useCallback(
    (item: SectionItem) => {
      const raw = String(item.image ?? '')
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      if (raw.startsWith('/')) return raw

      if (type === 'playlist') return `${apiBaseURL}/uploaded/playlists/${raw}`
      if (type === 'business') return `${apiBaseURL}/uploaded/businesses/${raw}`

      return `${apiBaseURL}/uploaded/${type}s/${raw}`
    },
    [type],
  )

  const handleItemClick = useCallback(
    (item: SectionItem) => {
      const itemId = getId(item)
      if (!itemId) return

      if (type === 'playlist') {
        navigate(`/playlist/${itemId}`)
        return
      }

      const routeType = TYPE_TO_ROUTE_SEGMENT[type]
      const uploadedDestination = TYPE_TO_UPLOADED_DESTINATION[type]
      const folderTitle = getTitleText(item)
      const folderImage = getImageUrl(item)

      try {
        localStorage.setItem(
          `plc:${routeType}:${itemId}`,
          JSON.stringify({
            title: folderTitle,
            image: folderImage,
            type: routeType,
            uploadedDestination,
          }),
        )
      } catch {
        // ignore storage error
      }

      navigate(`/playlist-collection/${routeType}/${itemId}`, {
        state: {
          title: folderTitle,
          image: folderImage,
          type: routeType,
          uploadedDestination,
        },
      })
    },
    [getId, getImageUrl, getTitleText, navigate, type],
  )

  const handleMoreClick = useCallback(() => {
    if (type === 'playlist') {
      navigate('/playlist-collection/playlist', {
        state: {
          type: 'playlist',
          title,
          categoryName: title,
        },
      })
      return
    }

    const routeType = TYPE_TO_ROUTE_SEGMENT[type]
    const uploadedDestination = TYPE_TO_UPLOADED_DESTINATION[type]

    navigate(`/playlist-collection/${routeType}`, {
      state: {
        type: routeType,
        title,
        categoryName: title,
        uploadedDestination,
      },
    })
  }, [navigate, title, type])

  const calcStep = useCallback(() => {
    const el = trackRef.current
    if (!el) return 280

    const card = el.querySelector<HTMLElement>('.ps-card, .ps-tile')
    if (!card) return Math.floor(el.clientWidth * 0.8)

    const styles = window.getComputedStyle(el)
    const gapValue = styles.columnGap || styles.gap || '0'
    const gap = Number.parseFloat(gapValue) || 0

    return Math.round(card.offsetWidth + gap)
  }, [])

  const scrollByDir = useCallback(
    (dir: -1 | 1) => {
      const el = trackRef.current
      if (!el) return
      el.scrollBy({ left: dir * calcStep(), behavior: 'smooth' })
    },
    [calcStep],
  )

  const updateArrows = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const left = el.scrollLeft
    const max = el.scrollWidth - el.clientWidth
    setCanLeft(left > 2)
    setCanRight(left < max - 2)
  }, [])

  useEffect(() => {
    if (isMood) return

    updateArrows()
    const el = trackRef.current
    if (!el) return

    const onScroll = () => updateArrows()
    el.addEventListener('scroll', onScroll, { passive: true })

    const onResize = () => updateArrows()
    window.addEventListener('resize', onResize)

    const resizeObserver = new ResizeObserver(() => updateArrows())
    resizeObserver.observe(el)

    const raf = requestAnimationFrame(() => updateArrows())

    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      resizeObserver.disconnect()
    }
  }, [safeItems, isMood, updateArrows])

  return (
    <section
      className={`ps ${isMood ? 'ps--mood' : isTiles ? 'ps--tiles' : 'ps--playlist'}`}
    >
      <div className="ps__head">
        <h2 className="ps__title">{title}</h2>

        {!isMood && (
          <div className="ps__controls">
            <button type="button" className="ps__more" onClick={handleMoreClick}>
              {t('actions.more', { defaultValue: 'More' })}
            </button>

            <button
              type="button"
              className="ps__arrowBtn"
              onClick={() => scrollByDir(-1)}
              disabled={!canLeft}
              aria-label={t('a11y.scrollLeft', { defaultValue: 'scroll left' })}
            >
              ‹
            </button>

            <button
              type="button"
              className="ps__arrowBtn"
              onClick={() => scrollByDir(1)}
              disabled={!canRight}
              aria-label={t('a11y.scrollRight', { defaultValue: 'scroll right' })}
            >
              ›
            </button>
          </div>
        )}
      </div>

      {isMood ? (
        <div className="ps-moodGrid">
          {safeItems.map((item) => {
            const titleText = getTitleText(item)
            const id = getId(item) || titleText
            const acc = getAccentById(id, 'mood')

            return (
              <button
                key={id}
                type="button"
                className="ps-pill"
                style={{ ['--acc' as string]: acc } as CSSProperties}
                onClick={() => handleItemClick(item)}
                title={titleText}
              >
                {titleText}
              </button>
            )
          })}
        </div>
      ) : (
        <div className="ps__track" ref={trackRef}>
          {safeItems.map((item) => {
            const titleText = getTitleText(item)
            const subText = getSubtitleText(item)
            const id = getId(item) || titleText

            if (!isTiles) {
              const grad = getGradientById(id, type)

              return (
                <button
                  key={id}
                  type="button"
                  className="ps-card"
                  onClick={() => handleItemClick(item)}
                  title={titleText}
                  style={
                    {
                      ['--card-a' as string]: grad.a,
                      ['--card-b' as string]: grad.b,
                    } as CSSProperties
                  }
                >
                  <div className="ps-card__imgWrap">
                    <img
                      className="ps-card__img"
                      src={getImageUrl(item)}
                      alt={titleText}
                      loading="lazy"
                    />
                  </div>

                  <div className="ps-card__text">
                    <div className="ps-card__name">{titleText}</div>
                    <div className="ps-card__sub">{subText}</div>
                  </div>
                </button>
              )
            }

            const grad = getGradientById(id, 'style')
            const imgUrl = getImageUrl(item)

            return (
              <button
                key={id}
                type="button"
                className="ps-tile"
                onClick={() => handleItemClick(item)}
                title={titleText}
                style={
                  {
                    ['--tile-a' as string]: grad.a,
                    ['--tile-b' as string]: grad.b,
                  } as CSSProperties
                }
              >
                <div className="ps-tile__label">
                  <span
                    className="ps-tile__title"
                    lang={(i18n.language || 'en').split('-')[0]}
                    style={
                      titleText.length > 16
                        ? {
                            fontSize: '19px',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }
                        : { whiteSpace: 'normal', overflowWrap: 'anywhere' }
                    }
                  >
                    {titleText}
                  </span>
                </div>

                <div className="ps-tile__artWrap" aria-hidden="true">
                  {imgUrl ? (
                    <img
                      className="ps-tile__art"
                      src={imgUrl}
                      alt={titleText}
                      loading="lazy"
                    />
                  ) : (
                    <div className="ps-tile__art ps-tile__art--empty" />
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
